import { useEffect, useState } from 'react'

import { fetchLocalJSON } from '../../../shared/utils/fetchLocalJSON.js'
import { Logger }         from '../../../shared/utils/logger.js'
import { FETCH_STATUS }   from '../../../shared/constants/fetchStatus.js'

const EMPTY_COMPARTMENTS = Object.freeze({})

/**
 * Loads the optional compartment-type dictionary from the project data package.
 *
 * The source file is `website/compartments.json`. On success, the hook exposes
 * its `compartments_types` object as `compartments`. Missing configuration, an
 * unavailable or empty file, or another loading failure produces an empty
 * dictionary with `status` set to
 * `FETCH_STATUS.ERROR`. Consumers can therefore disable compartment-dependent
 * features without treating the entire inventory as unavailable.
 *
 * A change of `dataUrl` resets the resource to its loading state and starts a
 * new request. Results from an obsolete request are ignored after cleanup.
 *
 * @param {Object} params
 * @param {string|null|undefined} params.dataUrl - Base URL of the local project data package.
 * @returns {{
 *   data: Record<string, unknown>,
 *   status: 'loading'|'ready'|'error',
 *   error: Error|null,
 * }} Compartment data and its loading state. The returned object remains stable until the state changes.
 */
export function useCompartments({ dataUrl }) {
  const [state, setState] = useState({
    data: EMPTY_COMPARTMENTS,
    status: FETCH_STATUS.LOADING,
    error: null,
  })

  useEffect(() => {
    if (!dataUrl) {
      setState({
        data: EMPTY_COMPARTMENTS,
        status: FETCH_STATUS.ERROR,
        error: new Error('Project data URL is unavailable'),
      })
      Logger.error({
        message: 'Project data URL is unavailable'
      })
      return
    }

    let cancelled = false

    setState({
      data: EMPTY_COMPARTMENTS,
      status: FETCH_STATUS.LOADING,
      error: null,
    })

    fetchLocalJSON(
      `${dataUrl}website/compartments.json`,
      'InventoryContext: compartments.json',
    )
      .then(data => {
        if (cancelled) return

        if (!data) {
          setState({
            data: EMPTY_COMPARTMENTS,
            status: FETCH_STATUS.ERROR,
            error: new Error('compartments.json not found or empty'),
          })
          Logger.error({message:'compartments.json not found or empty'})
          return
        }
        
        setState({
          data: data.compartments_types,
          status: FETCH_STATUS.READY,
          error: null,
        })
        Logger.info({message:'compartments.json loaded from .dev_data/'})
      })
      .catch(error => {
        if (cancelled) return

        setState({
          data: EMPTY_COMPARTMENTS,
          status: FETCH_STATUS.ERROR,
          error: error instanceof Error
            ? error
            : new Error(String(error)),
          })
        Logger.error({message:'error loading compartments.json', content: error})
      })

    return () => {
      cancelled = true
    }
  }, [dataUrl])

  return state
}
